import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useFavoritesContext } from '../context/FavoritesContext';
import toast from 'react-hot-toast';
import axios from 'axios';
import config from '../config';
import {
  LuArrowLeft,
  LuFilter,
  LuShare2,
  LuDownload,
  LuStar,
  LuX,
  LuChevronDown,
  LuChevronUp,
  LuSparkles,
} from 'react-icons/lu';
import { HiOutlineHeart, HiHeart } from 'react-icons/hi';
import { ImSpinner2 } from 'react-icons/im';

const apiUrl = config.apiUrl;

type SortOption = 'recent' | 'alphabetical' | 'votes' | 'shortlisted';
type FilterOption = 'all' | 'shortlisted' | 'voted';

interface ComparisonScore {
  memorability: number;
  brandability: number;
  seo_potential: number;
  pronounceability: number;
}

interface ComparisonResult {
  name: string;
  score: ComparisonScore;
  summary: string;
}

interface CompareResponse {
  comparison: ComparisonResult[];
  recommendation: string;
}

const FavoritesPage: React.FC = () => {
  const navigate = useNavigate();
  const {
    favorites,
    toggleFavorite,
    toggleShortlist,
    updateNotes,
    updateVote,
    getMetadata,
    getShortlisted,
  } = useFavoritesContext();

  // State
  const [sortBy, setSortBy] = useState<SortOption>('recent');
  const [filterBy, setFilterBy] = useState<FilterOption>('all');
  const [showFilters, setShowFilters] = useState(false);
  const [selectedForCompare, setSelectedForCompare] = useState<string[]>([]);
  const [showComparison, setShowComparison] = useState(false);
  const [comparisonResults, setComparisonResults] = useState<CompareResponse | null>(null);
  const [comparing, setComparing] = useState(false);
  const [expandedNotes, setExpandedNotes] = useState<string | null>(null);
  const [showShareModal, setShowShareModal] = useState(false);
  const [shareUrl, setShareUrl] = useState('');

  const shortlistedNames = getShortlisted();

  // Calculate stats
  const votedCount = favorites.filter((name) => getMetadata(name)?.vote).length;

  // Sort and filter favorites
  const sortedAndFilteredFavorites = useMemo(() => {
    let filtered = [...favorites];

    // Apply filter
    if (filterBy === 'shortlisted') {
      filtered = filtered.filter((name) => getMetadata(name)?.isShortlisted);
    } else if (filterBy === 'voted') {
      filtered = filtered.filter((name) => getMetadata(name)?.vote);
    }

    // Apply sort
    if (sortBy === 'alphabetical') {
      filtered.sort((a, b) => a.localeCompare(b));
    } else if (sortBy === 'votes') {
      filtered.sort((a, b) => {
        const aVote = getMetadata(a)?.vote || 0;
        const bVote = getMetadata(b)?.vote || 0;
        return bVote - aVote;
      });
    } else if (sortBy === 'shortlisted') {
      filtered.sort((a, b) => {
        const aShortlisted = getMetadata(a)?.isShortlisted || false;
        const bShortlisted = getMetadata(b)?.isShortlisted || false;
        if (aShortlisted === bShortlisted) return 0;
        return aShortlisted ? -1 : 1;
      });
    } else if (sortBy === 'recent') {
      filtered.sort((a, b) => {
        const aTime = getMetadata(a)?.addedAt || 0;
        const bTime = getMetadata(b)?.addedAt || 0;
        return bTime - aTime;
      });
    }

    return filtered;
  }, [favorites, sortBy, filterBy, getMetadata]);

  // Vote on a name
  const handleVote = (name: string, stars: number) => {
    updateVote(name, stars);
    toast.success(`Voted ${stars} star${stars !== 1 ? 's' : ''} for ${name}`);
  };

  // Toggle compare selection
  const toggleCompareSelection = (name: string) => {
    setSelectedForCompare((prev) => {
      if (prev.includes(name)) {
        return prev.filter((n) => n !== name);
      } else if (prev.length < 3) {
        return [...prev, name];
      } else {
        toast.error('You can compare up to 3 names at a time');
        return prev;
      }
    });
  };

  // Compare selected names
  const handleCompare = async () => {
    if (selectedForCompare.length < 2) {
      toast.error('Select at least 2 names to compare');
      return;
    }

    setComparing(true);
    try {
      const res = await axios.post<CompareResponse>(`${apiUrl}/compare-names`, {
        names: selectedForCompare,
      });
      setComparisonResults(res.data);
      setShowComparison(true);
    } catch (err: any) {
      console.error('Comparison failed:', err);
      toast.error(err.response?.data?.error || 'Failed to compare names');
    } finally {
      setComparing(false);
    }
  };

  // Export to CSV
  const handleExport = () => {
    const headers = ['Name', 'Shortlisted', 'Vote', 'Tags', 'Notes', 'Added At'];
    const rows = favorites.map((name) => {
      const meta = getMetadata(name);
      return [
        name,
        meta?.isShortlisted ? 'Yes' : 'No',
        meta?.vote || '',
        meta?.tags?.join('; ') || '',
        meta?.notes || '',
        meta?.addedAt ? new Date(meta.addedAt).toLocaleString() : '',
      ];
    });

    const csvContent =
      [headers, ...rows].map((row) => row.map((cell) => `"${cell}"`).join(',')).join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `favorites-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);

    toast.success('Exported to CSV');
  };

  // Share session
  const handleShare = () => {
    const sessionData = {
      favorites,
      metadata: favorites.reduce((acc, name) => {
        const meta = getMetadata(name);
        if (meta) acc[name] = meta;
        return acc;
      }, {} as Record<string, any>),
      timestamp: Date.now(),
    };

    // Generate a unique ID
    const sessionId = Math.random().toString(36).substring(2, 15);
    
    // Store in localStorage (in production, this would be a backend call)
    const allSessions = JSON.parse(localStorage.getItem('shared_sessions') || '{}');
    allSessions[sessionId] = sessionData;
    localStorage.setItem('shared_sessions', JSON.stringify(allSessions));

    const url = `${window.location.origin}/share/${sessionId}`;
    setShareUrl(url);
    setShowShareModal(true);

    // Copy to clipboard
    navigator.clipboard.writeText(url);
    toast.success('Share link copied to clipboard!');
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      {/* Header Section */}
      <div className="bg-white border-b border-gray-200 px-4 sm:px-6 py-4 sm:py-6">
        <div className="max-w-4xl mx-auto">
          <button
            onClick={() => navigate('/app')}
            className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-4 transition"
          >
            <LuArrowLeft className="w-4 h-4" />
            <span className="text-sm font-medium">Back to Generator</span>
          </button>

          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
            <div className="flex-1">
              <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">
                Your Favorites
              </h1>
              <p className="text-gray-600 mt-1 sm:mt-2 text-sm sm:text-base">
                Compare, vote, and decide on your perfect name
              </p>
            </div>

            {favorites.length > 0 && (
              <div className="flex flex-wrap gap-2">
                <button
                  onClick={() => setShowFilters(!showFilters)}
                  className="flex items-center gap-2 px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition font-medium text-sm"
                >
                  <LuFilter className="w-4 h-4" />
                  {showFilters ? 'Hide' : 'Show'} Filters
                </button>
                <button
                  onClick={handleExport}
                  className="flex items-center gap-2 px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition font-medium text-sm"
                >
                  <LuDownload className="w-4 h-4" />
                  Export
                </button>
                <button
                  onClick={handleShare}
                  className="flex items-center gap-2 px-4 py-2 text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition font-medium text-sm"
                >
                  <LuShare2 className="w-4 h-4" />
                  Share
                </button>
              </div>
            )}
          </div>

          {/* Stats Bar */}
          {favorites.length > 0 && (
            <div className="mt-4 flex flex-wrap items-center gap-4 sm:gap-6 text-sm text-gray-600">
              <div>
                <span className="font-semibold text-gray-900">{favorites.length}</span>{' '}
                favorite{favorites.length !== 1 ? 's' : ''}
              </div>
              {shortlistedNames.length > 0 && (
                <div>
                  <span className="font-semibold text-pink-600">
                    {shortlistedNames.length}
                  </span>{' '}
                  shortlisted
                </div>
              )}
              {votedCount > 0 && (
                <div>
                  <span className="font-semibold text-yellow-600">{votedCount}</span> voted
                </div>
              )}
              {selectedForCompare.length > 0 && (
                <div>
                  <span className="font-semibold text-blue-600">
                    {selectedForCompare.length}
                  </span>{' '}
                  selected
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Filters Section */}
      {showFilters && favorites.length > 0 && (
        <div className="bg-white border-b border-gray-200 px-4 sm:px-6 py-4">
          <div className="max-w-4xl mx-auto space-y-4">
            {/* Sort Options */}
            <div className="flex flex-col sm:flex-row sm:items-center gap-2">
              <span className="text-sm font-medium text-gray-700">Sort by:</span>
              <div className="flex flex-wrap gap-2">
                {[
                  { value: 'recent', label: 'Recent' },
                  { value: 'alphabetical', label: 'A-Z' },
                  { value: 'votes', label: 'Votes' },
                  { value: 'shortlisted', label: 'Shortlisted First' },
                ].map((option) => (
                  <button
                    key={option.value}
                    onClick={() => setSortBy(option.value as SortOption)}
                    className={`px-3 py-1.5 rounded-lg text-sm font-medium transition ${
                      sortBy === option.value
                        ? 'bg-blue-100 text-blue-700'
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Filter Options */}
            <div className="flex flex-col sm:flex-row sm:items-center gap-2">
              <span className="text-sm font-medium text-gray-700">Show:</span>
              <div className="flex flex-wrap gap-2">
                {[
                  { value: 'all', label: 'All' },
                  { value: 'shortlisted', label: 'Shortlisted Only' },
                  { value: 'voted', label: 'Voted Only' },
                ].map((option) => (
                  <button
                    key={option.value}
                    onClick={() => setFilterBy(option.value as FilterOption)}
                    className={`px-3 py-1.5 rounded-lg text-sm font-medium transition ${
                      filterBy === option.value
                        ? 'bg-blue-100 text-blue-700'
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Compare Bar */}
      {selectedForCompare.length >= 2 && !showComparison && (
        <div className="bg-blue-50 border-b border-blue-200 px-4 sm:px-6 py-3">
          <div className="max-w-4xl mx-auto flex items-center justify-between">
            <div className="text-sm font-medium text-blue-900">
              {selectedForCompare.length} name{selectedForCompare.length !== 1 ? 's' : ''}{' '}
              selected for comparison
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => setSelectedForCompare([])}
                className="px-3 py-1.5 text-sm text-blue-700 hover:text-blue-900 font-medium"
              >
                Clear
              </button>
              <button
                onClick={handleCompare}
                disabled={comparing}
                className="px-4 py-1.5 bg-blue-600 hover:bg-blue-700 text-white text-sm rounded-lg font-medium transition disabled:opacity-50 flex items-center gap-2"
              >
                {comparing ? (
                  <>
                    <ImSpinner2 className="animate-spin" />
                    Comparing...
                  </>
                ) : (
                  <>
                    <LuSparkles className="w-4 h-4" />
                    Compare Names
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Content Area */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-6 sm:py-8 flex-1 w-full">
        {favorites.length === 0 ? (
          /* Empty State */
          <div className="text-center py-16">
            <div className="text-6xl mb-4">⭐</div>
            <h3 className="text-xl font-semibold text-gray-800 mb-2">No favorites yet</h3>
            <p className="text-gray-500 mb-6">
              Save a few names from the generator to review them here
            </p>
            <button
              onClick={() => navigate('/app')}
              className="px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition"
            >
              Go to Generator
            </button>
          </div>
        ) : showComparison && comparisonResults ? (
          /* Comparison Results View */
          <div>
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-gray-900">Comparison Results</h2>
              <button
                onClick={() => {
                  setShowComparison(false);
                  setComparisonResults(null);
                }}
                className="text-gray-600 hover:text-gray-900 flex items-center gap-2 text-sm font-medium"
              >
                <LuX className="w-4 h-4" />
                Close
              </button>
            </div>

            {/* AI Recommendation */}
            <div className="mb-6 p-6 bg-gradient-to-r from-blue-50 to-purple-50 border-2 border-blue-200 rounded-xl">
              <div className="flex items-start gap-3">
                <LuSparkles className="w-6 h-6 text-blue-600 flex-shrink-0 mt-1" />
                <div>
                  <h3 className="text-lg font-bold text-gray-900 mb-2">
                    AI Recommendation
                  </h3>
                  <p className="text-gray-700">{comparisonResults.recommendation}</p>
                </div>
              </div>
            </div>

            {/* Comparison Cards */}
            <div className="grid gap-6 md:grid-cols-2">
              {comparisonResults.comparison.map((result) => (
                <div
                  key={result.name}
                  className="bg-white border-2 border-gray-200 rounded-xl p-6 hover:shadow-lg transition-all"
                >
                  <h3 className="text-2xl font-bold text-gray-900 mb-4">
                    {result.name}
                  </h3>

                  {/* Scores Grid */}
                  <div className="grid grid-cols-2 gap-3 mb-4">
                    {Object.entries(result.score).map(([key, value]) => (
                      <div
                        key={key}
                        className="bg-gray-50 rounded-lg p-3 text-center"
                      >
                        <div className="text-2xl font-bold text-blue-600">
                          {value}
                          <span className="text-sm text-gray-500">/10</span>
                        </div>
                        <div className="text-xs text-gray-600 capitalize mt-1">
                          {key.replace('_', ' ')}
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Summary */}
                  <div className="pt-4 border-t border-gray-200">
                    <p className="text-sm text-gray-600 italic">{result.summary}</p>
                  </div>

                  {/* Action Button */}
                  <button
                    onClick={() => {
                      toggleShortlist(result.name);
                      toast.success(`${result.name} added to shortlist!`);
                    }}
                    className="mt-4 w-full px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition"
                  >
                    Choose This One
                  </button>
                </div>
              ))}
            </div>

            {/* Back Button */}
            <div className="mt-6 text-center">
              <button
                onClick={() => {
                  setShowComparison(false);
                  setSelectedForCompare([]);
                }}
                className="px-6 py-2 text-gray-600 hover:text-gray-900 font-medium"
              >
                ← Back to Favorites
              </button>
            </div>
          </div>
        ) : sortedAndFilteredFavorites.length === 0 ? (
          /* Empty Filtered State */
          <div className="text-center py-12">
            <div className="text-4xl mb-3">🔍</div>
            <h3 className="text-lg font-semibold text-gray-800 mb-2">
              No names match your filters
            </h3>
            <p className="text-gray-500 mb-4">
              Try adjusting your filters to see more results
            </p>
            <button
              onClick={() => {
                setFilterBy('all');
                setSortBy('recent');
              }}
              className="text-blue-600 hover:text-blue-700 font-medium text-sm"
            >
              Reset Filters
            </button>
          </div>
        ) : (
          /* Favorites List */
          <div className="space-y-4">
            {sortedAndFilteredFavorites.map((name) => {
              const metadata = getMetadata(name);
              const isShortlisted = metadata?.isShortlisted || false;
              const vote = metadata?.vote || 0;
              const isExpanded = expandedNotes === name;
              const isSelected = selectedForCompare.includes(name);

              return (
                <div
                  key={name}
                  className={`border rounded-xl p-5 bg-white hover:shadow-md transition-all ${
                    isShortlisted
                      ? 'border-pink-300 ring-2 ring-pink-100'
                      : 'border-gray-200'
                  } ${isSelected ? 'ring-2 ring-blue-300 border-blue-400' : ''}`}
                >
                  {/* Name Header and Actions */}
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-start gap-3 flex-1">
                      {/* Compare Checkbox */}
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => toggleCompareSelection(name)}
                        className="mt-1 w-5 h-5 text-blue-600 border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
                      />
                      <div className="flex-1">
                        <h3 className="text-xl font-semibold text-gray-900 break-all">
                          {name}
                        </h3>
                      </div>
                    </div>

                    {/* Action Buttons */}
                    <div className="flex items-center gap-2 ml-4">
                      {/* Shortlist Button */}
                      <button
                        onClick={() => toggleShortlist(name)}
                        className={`p-2 rounded-lg transition ${
                          isShortlisted
                            ? 'text-pink-600 bg-pink-100 hover:bg-pink-200'
                            : 'text-gray-400 hover:text-pink-600 hover:bg-pink-50'
                        }`}
                        title={
                          isShortlisted ? 'Remove from shortlist' : 'Add to shortlist'
                        }
                      >
                        {isShortlisted ? (
                          <HiHeart className="w-5 h-5" />
                        ) : (
                          <HiOutlineHeart className="w-5 h-5" />
                        )}
                      </button>

                      {/* Remove Button */}
                      <button
                        onClick={() => toggleFavorite(name)}
                        className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition"
                        title="Remove from favorites"
                      >
                        <LuX className="w-5 h-5" />
                      </button>
                    </div>
                  </div>

                  {/* Voting UI */}
                  <div className="mb-3 flex items-center gap-2">
                    <span className="text-sm text-gray-600">Your vote:</span>
                    <div className="flex gap-1">
                      {[1, 2, 3, 4, 5].map((stars) => (
                        <button
                          key={stars}
                          onClick={() => handleVote(name, stars)}
                          className={`p-1 rounded transition ${
                            vote >= stars
                              ? 'text-yellow-500 hover:text-yellow-600'
                              : 'text-gray-300 hover:text-yellow-400'
                          }`}
                        >
                          <LuStar
                            className="w-5 h-5"
                            fill={vote >= stars ? 'currentColor' : 'none'}
                          />
                        </button>
                      ))}
                    </div>
                    {vote > 0 && (
                      <span className="text-sm text-gray-500 ml-2">
                        ({vote} star{vote !== 1 ? 's' : ''})
                      </span>
                    )}
                  </div>

                  {/* Tags */}
                  {metadata?.tags && metadata.tags.length > 0 && (
                    <div className="mb-3 flex flex-wrap gap-2">
                      {metadata.tags.map((tag) => (
                        <span
                          key={tag}
                          className="inline-flex items-center gap-1 px-2 py-1 bg-blue-100 text-blue-700 rounded text-xs font-medium"
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                  )}

                  {/* Notes */}
                  <div>
                    <button
                      onClick={() =>
                        setExpandedNotes(isExpanded ? null : name)
                      }
                      className="text-sm text-gray-600 hover:text-gray-900 flex items-center gap-1 mb-2"
                    >
                      {isExpanded ? (
                        <LuChevronUp className="w-4 h-4" />
                      ) : (
                        <LuChevronDown className="w-4 h-4" />
                      )}
                      Notes
                    </button>
                    {isExpanded && (
                      <textarea
                        value={metadata?.notes || ''}
                        onChange={(e) => updateNotes(name, e.target.value)}
                        placeholder="Add notes about this name..."
                        className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                        rows={3}
                      />
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Share Modal */}
      {showShareModal && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 px-4"
          onClick={() => setShowShareModal(false)}
        >
          <div
            className="bg-white rounded-xl shadow-2xl max-w-md w-full p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-bold text-gray-900">Share Your Favorites</h3>
              <button
                onClick={() => setShowShareModal(false)}
                className="text-gray-400 hover:text-gray-600 transition"
              >
                <LuX className="w-5 h-5" />
              </button>
            </div>

            <p className="text-gray-600 mb-4">
              Share this link with others to collaborate on name selection:
            </p>

            <div className="bg-gray-50 rounded-lg p-3 mb-4 border border-gray-200">
              <code className="text-sm text-gray-800 break-all">{shareUrl}</code>
            </div>

            <button
              onClick={() => {
                navigator.clipboard.writeText(shareUrl);
                toast.success('Link copied!');
              }}
              className="w-full px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition flex items-center justify-center gap-2"
            >
              <LuShare2 className="w-4 h-4" />
              Copy Link
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default FavoritesPage;

